import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { navItems } from "@/mocks/site";

interface SiteHeaderProps {
  logoAsHeading?: boolean;
}

export default function SiteHeader({ logoAsHeading = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const isActive = (path: string) =>
    path === "/" ? location.pathname === "/" : location.pathname.startsWith(path);

  const logoInner = (
    <>
      <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-background-50 font-heading text-lg font-black">
        15
      </span>
      <span className="flex flex-col leading-none">
        <span className="font-heading text-xl font-black tracking-wide text-foreground-950">
          15黑料网
        </span>
        <span className="label-caps mt-1 text-[10px] text-foreground-500">
          Deep Read
        </span>
      </span>
    </>
  );

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors duration-300 ${
        scrolled
          ? "border-background-200 bg-background-50/95 backdrop-blur"
          : "border-transparent bg-background-50"
      }`}
    >
      <div className="mx-auto flex h-16 w-full max-w-[1400px] items-center justify-between px-4 md:px-8">
        <div className="flex items-center gap-3">
          {logoAsHeading ? (
            <h1 className="m-0">
              <Link
                to="/"
                className="flex items-center gap-2.5 cursor-pointer"
                aria-label="15黑料网 首页"
              >
                {logoInner}
              </Link>
            </h1>
          ) : (
            <Link
              to="/"
              className="flex items-center gap-2.5 cursor-pointer"
              aria-label="15黑料网 首页"
            >
              {logoInner}
            </Link>
          )}
        </div>

        <nav aria-label="主导航" className="hidden items-center gap-1 md:flex">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`whitespace-nowrap rounded-md px-3.5 py-2 text-sm transition-colors cursor-pointer ${
                isActive(item.path)
                  ? "bg-primary-50 text-primary-700 font-medium"
                  : "text-foreground-700 hover:bg-background-100 hover:text-foreground-950"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="hidden items-center gap-3 md:flex">
          <Link
            to="/#subscribe"
            className="whitespace-nowrap rounded-md bg-primary-500 px-4 py-2 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
          >
            订阅深度解读
          </Link>
        </div>

        <button
          type="button"
          aria-label={menuOpen ? "关闭菜单" : "打开菜单"}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen((prev) => !prev)}
          className="flex h-10 w-10 items-center justify-center rounded-md text-foreground-800 transition-colors hover:bg-background-100 md:hidden cursor-pointer"
        >
          <i className={menuOpen ? "ri-close-line text-xl" : "ri-menu-line text-xl"} />
        </button>
      </div>

      {menuOpen ? (
        <div className="border-t border-background-200 bg-background-50 md:hidden">
          <nav aria-label="移动端导航" className="flex flex-col px-4 py-3">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`whitespace-nowrap rounded-md px-3 py-3 text-sm transition-colors cursor-pointer ${
                  isActive(item.path)
                    ? "bg-primary-50 text-primary-700 font-medium"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {item.label}
              </Link>
            ))}
            <Link
              to="/#subscribe"
              className="mt-2 whitespace-nowrap rounded-md bg-primary-500 px-3 py-3 text-center text-sm font-medium text-background-50 cursor-pointer"
            >
              订阅深度解读
            </Link>
          </nav>
        </div>
      ) : null}
    </header>
  );
}